import React, { useState, useEffect } from "react";
import { Button, Form, Alert } from "react-bootstrap";
import { editShareholderRequest, createShareholderRequest } from "@/services";
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { useParams } from "react-router-dom";

const ShareholderForm = ({ initialData, onCancel, reloadShareholders, isCreditOfferApproved = false, otherShareholdingTotal = 0, parentCompany = null, uboThreshold = null }) => {
  const { applicationId } = useParams();
  // Only an individual can hold shares in a business shareholder of the borrower company.
  const isCompanyShareholder = !!parentCompany;
  // Above the threshold the backend always treats the shareholder as a beneficial
  // owner, so the toggle is only offered below it.
  const isAlwaysBeneficialOwner = (holding) =>
    uboThreshold != null && Number(holding || 0) > uboThreshold;
  const [formData, setFormData] = useState({
    loan_id: applicationId,
    name: "",
    email: "",
    phone_number: "",
    shareholding_percentage: "",
    shareholder_type: "INDIVIDUAL",
    identity_type: "PASSPORT",
    identity: "",
    is_business_owner: false,
    business_shareholder: parentCompany?.uuid || null,
  });
  const isShareholderKycVerified = !!initialData?.is_kyc_verified;
  const isLocked = isShareholderKycVerified || isCreditOfferApproved;

  const [errors, setErrors] = useState(null); // for API errors
  const [submitting, setSubmitting] = useState(false); // to disable submit while saving

  useEffect(() => {
    if (initialData) {
      setFormData({ ...initialData, loan_id: applicationId });
    }
  }, [initialData]);

  const holdingLabel = isCompanyShareholder
    ? `Share of ${parentCompany.name} (%)`
    : "Shareholding (%)";

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "shareholder_type") {
      setFormData((prev) => ({
        ...prev,
        shareholder_type: value,
        identity_type: value === "BUSINESS" ? "CR_NUMBER" : "PASSPORT",
      }));
    }
    if (name === "shareholding_percentage") {
      setFormData((prev) => ({
        ...prev,
        shareholding_percentage: value,
        is_business_owner: isAlwaysBeneficialOwner(value) ? true : prev.is_business_owner,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]:
          name === "email" && typeof value === "string"
            ? value.toLowerCase()
            : value,
      }));
    }
  };
  const handleAddOrUpdate = async (shareholder) => {
    setSubmitting(true);
    setErrors(null);

    try {
      let res;

      if (initialData) {
        // Edit existing shareholder
        res = await editShareholderRequest(initialData.uuid, shareholder);
      } else {
        // Create new shareholder
        res = await createShareholderRequest({ ...shareholder });
      }

      // Handle API-level errors
      if (res?.success === false) {
        setErrors(res.error || { general: res.message || "Something went wrong" });
        // An admin may have verified this shareholder while the form was open,
        // in which case the refreshed row comes back locked.
        if (initialData) await reloadShareholders();
        return; // stop further execution
      }

      await reloadShareholders();
      onCancel();
      // Reset form if adding new
      if (!initialData) {
        setFormData({
          name: "",
          shareholding_percentage: "",
          shareholder_type: "INDIVIDUAL",
          identity_type: "PASSPORT",
          identity: "",
          business_shareholder: parentCompany?.uuid || null,
        });
      }
    } catch (err) {
      console.error("Unexpected error saving shareholder:", err);
      setErrors({ general: "Something went wrong. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isLocked) return;
    const newTotal = otherShareholdingTotal + Number(formData.shareholding_percentage || 0);
    if (Math.round(newTotal * 100) > 10000) {
      const remaining = Math.max(0, Math.round((100 - otherShareholdingTotal) * 100) / 100);
      const pool = isCompanyShareholder ? `Shareholders of ${parentCompany.name}` : "Total shareholding";
      setErrors({
        shareholding_percentage: [
          `${pool} can't exceed 100%. You can add up to ${remaining}% for this shareholder.`,
        ],
      });
      return;
    }
    handleAddOrUpdate(formData);
  };

  return (
    <Form onSubmit={handleSubmit} className="p-3 border rounded shadow-sm">
      <h5 className="mb-3">
        {initialData ? "Edit Shareholder" : "Add Shareholder"}
        {isCompanyShareholder ? ` of ${parentCompany.name}` : ""}
      </h5>

      {isShareholderKycVerified && (
        <Alert variant="info">
          This shareholder&apos;s KYC has been verified, so these details can no longer be changed.
        </Alert>
      )}

      {/* Show general errors */}
      {errors?.general && <Alert variant="danger">{errors.general}</Alert>}
      <fieldset disabled={isLocked}>
        {/* Shareholder Type */}
        {!isCompanyShareholder && (
          <Form.Group className="mb-3" controlId="shareholder-form-type">
            <Form.Label>
              Shareholder Type <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              name="shareholder_type"
              value={formData.shareholder_type}
              onChange={handleChange}
              required
            >
              <option value="INDIVIDUAL">Individual</option>
              <option value="BUSINESS">Business</option>
            </Form.Select>
            {errors?.shareholder_type && (
              <div className="text-danger">{errors.shareholder_type.join(", ")}</div>
            )}
          </Form.Group>
        )}

        {/* Name */}
        <Form.Group className="mb-3" controlId="shareholder-form-name">
          <Form.Label>
            Name <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control name="name" value={formData.name} onChange={handleChange} required />
          {errors?.name && <div className="text-danger">{errors.name.join(", ")}</div>}
        </Form.Group>

        {/* Shareholding */}
        <Form.Group className="mb-3" controlId="shareholder-form-shareholding">
          <Form.Label>
            {holdingLabel} <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="number"
            name="shareholding_percentage"
            value={formData.shareholding_percentage}
            onChange={handleChange}
            min="0.01" // ✅ disallows 0, minimum is 0.01
            max="100"
            step="0.01" // ✅ allows decimals up to 2 places
            required
            pattern="^(?!0(\.0{1,2})?$)\d{1,3}(\.\d{1,2})?$" // ✅ no 0 or 0.00
          />
          {errors?.shareholding_percentage && (
            <div className="text-danger">{errors.shareholding_percentage.join(", ")}</div>
          )}
        </Form.Group>

        {!isAlwaysBeneficialOwner(formData.shareholding_percentage) && (
          <Form.Group className="mb-3">
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="tooltip-switch"> Marking a shareholder as a Beneficial Owner requires KYC submission.</Tooltip>}
            >
              <Form.Check
                id="shareholder-form-beneficial-owner"
                style={{ cursor: 'pointer' }}
                type="switch"
                label="Beneficiary owner"
                checked={formData.is_business_owner}
                onChange={() =>
                  setFormData((prev) => ({ ...prev, is_business_owner: !formData.is_business_owner }))
                }
                className="mb-3"
              />
            </OverlayTrigger>
          </Form.Group>

        )}
      </fieldset>
      {/* Buttons */}
      <div className="d-flex justify-content-end gap-2">
        {onCancel && (
          <Button id="b_cancel_shareholder_form" variant="secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
        )}
        <Button id="b_submit_shareholder_form" type="submit" variant="primary" disabled={submitting || isLocked}>
          {submitting ? "Saving..." : "Save"}
        </Button>
      </div>
    </Form>
  );
};

export default ShareholderForm;
